import { ReactNode, useRef, useState } from 'react';
import { CSSProperties } from 'react';
import { Detail, Tooltip } from '@ets/design-system';
import { usePublicProfile } from '_common/hooks';
import { SuiteAvatar } from '_common/suite/components';
import UserAvatar, { UserAvatarProps } from '../UserAvatar/UserAvatar';
import UsernameLabel from '../Labels/UsernameLabel/UsernameLabel';
import styles from './UserCard.module.scss';

type UserCardProps = {
  userId: UserId;
  userName?: string;
  appendToName?: ReactNode;
  editor?: boolean;
  online?: boolean;
  titleStyle?: CSSProperties;
  color?: CSSProperties['color'];
  size?: UserAvatarProps['size'];
  avatarStyle?: CSSProperties;
};

const UserCard = ({
  userId,
  userName,
  appendToName = null,
  editor = false,
  size = 'm',
  online = false,
  color,
  titleStyle,
  avatarStyle,
}: UserCardProps) => {
  const [nameWrapped, setNameWrapped] = useState(false);
  const [emailWrapped, setEmailWrapped] = useState(false);
  const nameRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLDivElement>(null);

  const profile = usePublicProfile(userId);

  const showTooltip = () => {
    if (emailRef.current) {
      if (emailRef.current.scrollWidth > emailRef.current.clientWidth) {
        setEmailWrapped(true);
      } else {
        setEmailWrapped(false);
      }
    }
    if (nameRef.current) {
      if (nameRef.current.scrollWidth > nameRef.current.clientWidth) {
        setNameWrapped(true);
      } else {
        setNameWrapped(false);
      }
    }
  };

  return (
    <div
      className={`${styles.root} ${size && styles[size]} ${online && styles.online}`}
      data-testid={`${userId}-user-card`}
    >
      <div className={styles.avatar} style={avatarStyle}>
        {editor ? (
          <SuiteAvatar userId={userId} size={size} online={online} />
        ) : (
          <UserAvatar userId={userId} size={size} online={online} />
        )}
      </div>
      <Tooltip
        placement="bottom"
        visible={nameWrapped || emailWrapped}
        content={`${nameWrapped ? `${profile.name}\n` : ''}${
          emailWrapped ? `${profile.email}` : ''
        }`}
        testId={`user-card-${userId}-username`}
      >
        <div
          className={`${styles.container} ${size && styles[size]}`}
          style={{ color }}
          onMouseEnter={showTooltip}
        >
          <div className={`${styles.name} ${styles.ellipsis}`} ref={nameRef}>
            <span style={{ ...titleStyle }}>
              <UsernameLabel
                userId={profile.type !== 'removed' ? userId : undefined}
                userName={profile.type === 'removed' ? userName : undefined}
              />
            </span>
            {appendToName}
          </div>

          {profile.email && (
            <Detail
              className={`${styles.username} ${styles.ellipsis}`}
              ref={emailRef}
              testId={`${userId}-user-card-email`}
            >
              {profile.email}
            </Detail>
          )}
        </div>
      </Tooltip>
    </div>
  );
};

export default UserCard;
